import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { ParsedApiError } from '@loctopus/shared';
import { ErrorSummary } from '@loctopus/ui';
import { Button, Card, FormField, Input } from '../../components/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';

/**
 * Redeems the single-use, 48h-expiry token `ForgotPasswordPage` requested
 * (`POST /dashboard/auth/reset-password`). On success, the server has already invalidated every
 * session issued before this moment (same `sessionsInvalidatedAt` mechanism the 2FA rebuild's
 * forced-reset routes use) and does NOT issue a new session itself - this page sends the person
 * to the ordinary login screen to sign in fresh with their new password, rather than silently
 * establishing a session from a possibly-shared/compromised email link.
 */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<ParsedApiError | string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    if (password.length < 12) {
      setError('Password must be at least 12 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      await api.auth.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(toParsedError(err));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-bg px-4">
        <Card className="w-full max-w-sm" title="Reset your password">
          <p className="text-sm text-slate-600">
            This reset link is missing its token. Request a new one from{' '}
            <Link to="/forgot-password" className="text-brand-primary underline">
              the forgot-password page
            </Link>
            .
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg px-4">
      <Card className="w-full max-w-sm" title="Reset your password">
        {done ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-slate-600">
              Your password has been reset, and every previous session was signed out. Sign in
              again with your new password.
            </p>
            <Button className="w-full justify-center" onClick={() => navigate('/login')}>
              Go to sign in
            </Button>
          </div>
        ) : (
          <form className="flex flex-col gap-1" onSubmit={handleSubmit}>
            <FormField label="New password" htmlFor="reset-password" hint="At least 12 characters">
              <Input
                id="reset-password"
                type="password"
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </FormField>
            <FormField label="Confirm new password" htmlFor="reset-password-confirm">
              <Input
                id="reset-password-confirm"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </FormField>
            {error && (typeof error === 'string' ? <ErrorBannerText message={error} /> : <ErrorSummary error={error} className="mb-1" />)}
            <Button type="submit" disabled={submitting} className="w-full justify-center mt-2">
              {submitting ? 'Resetting...' : 'Reset password'}
            </Button>
          </form>
        )}
      </Card>
    </div>
  );
}

function ErrorBannerText({ message }: { message: string }) {
  return <p className="mb-1 text-sm text-red-600">{message}</p>;
}
